import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from './index';
import { fetchMenu } from '../modules/menu/menuApi';
import { useSwitchRoleMutation } from '../modules/userManagement/admin/adminApi';
import {
  fetchUserDetails,
  updateUserLocally,
} from '../modules/user/profileSlice';

type RoleGroup = {
  groupId: number;
  groupName?: string;
  groupCode?: string;
};

const SUPER_ADMIN_ROLE_CODES = ['HBADMIN', 'SUPER_ADMIN', 'SUPERADMIN'];
const normalizeRoleCode = (code?: string) => code?.trim().toUpperCase() ?? '';

export const useRoleSwitcher = () => {
  const details = useAppSelector((state) => state.profile.details);
  const dispatch = useAppDispatch();
  const [switchRole, { isLoading }] = useSwitchRoleMutation();

  const normalizedGroups = useMemo<RoleGroup[]>(() => {
    if (details?.groups && Array.isArray(details.groups)) {
      return details.groups.map((group: RoleGroup) => ({
        ...group,
        groupCode: normalizeRoleCode(group.groupCode),
      }));
    }
    if (details?.groupId) {
      return [
        {
          groupId: Number(details.groupId),
          groupName: details.groupName,
          groupCode: normalizeRoleCode(details.groupCode),
        },
      ];
    }
    return [];
  }, [details]);

  const isSuperAdmin = useMemo(() => {
    const primaryGroupCode = normalizeRoleCode(details?.groupCode);
    if (SUPER_ADMIN_ROLE_CODES.includes(primaryGroupCode)) {
      return true;
    }
    return normalizedGroups.some((group) =>
      SUPER_ADMIN_ROLE_CODES.includes(group.groupCode || '')
    );
  }, [details?.groupCode, normalizedGroups]);

  const groups = useMemo<RoleGroup[]>(() => {
    return normalizedGroups;
  }, [normalizedGroups]);

  const activeGroupId = useMemo(() => {
    const storedGroupId = Number(
      details?.activeGroupId ?? details?.groupId ?? 0
    );
    if (
      storedGroupId &&
      groups.some((group) => group.groupId === storedGroupId)
    ) {
      return storedGroupId;
    }
    return groups[0]?.groupId ?? 0;
  }, [details?.activeGroupId, details?.groupId, groups]);

  const activeGroup = useMemo(
    () => groups.find((group) => group.groupId === activeGroupId) ?? null,
    [groups, activeGroupId]
  );
  const activeRoleCode =
    activeGroup?.groupCode ?? normalizeRoleCode(details?.groupCode);

  const handleRoleSelect = useCallback(
    async (groupId: number) => {
      if (!groupId || groupId === activeGroupId || isLoading) {
        return;
      }
      const response = await switchRole({ targetGroupId: groupId }).unwrap();
      const accessToken = response?.data?.access_token;
      if (accessToken) {
        localStorage.setItem('access_token', accessToken);
      }
      localStorage.setItem('active_group_id', String(groupId));
      const nextGroup = groups.find((group) => group.groupId === groupId);
      const nextCode = normalizeRoleCode(nextGroup?.groupCode);
      if (nextCode) {
        localStorage.setItem('active_group_code', nextCode);
      }
      dispatch(
        updateUserLocally({
          groupId: groupId,
          activeGroupId: groupId,
          groupCode: nextCode,
          groupName: nextGroup?.groupName,
        })
      );
      dispatch(fetchUserDetails());
      dispatch(fetchMenu());
    },
    [activeGroupId, dispatch, groups, isLoading, switchRole]
  );

  return {
    activeRoleCode,
    activeGroupId,
    groups,
    isSwitchingRole: isLoading,
    onSwitchRole: handleRoleSelect,
    isSuperAdmin,
  };
};
